import DragAndDropFiles from 'Body/DragAndDropFiles/DragAndDropFiles';
import { fetchCandidates } from 'Services/CandidatesAPI';
import { useAuth0 } from 'auth0/reactAuth0Spa';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Dropzone, { useDropzone } from 'react-dropzone';
import style from '../style';
import Swal from 'sweetalert2';
import { DepartmentContext } from 'DepartmentWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faFile,
  faFileImage,
  faFilePdf,
  faInfoCircle,
  faRedo,
  faRedoAlt,
  faSortDown,
  faSpinner,
  faTimesCircle,
  faUndoAlt,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { constructUrl, fetchDataAgnostic, postData } from 'Services/dataApi';
import { checkLoginForError, formatBearerToken, sanitizeFilename } from 'Common.functions';
import {
  Button,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Popover,
  PopoverBody,
  UncontrolledTooltip,
} from 'reactstrap';
import axios from 'axios';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import DocumentTypesPopover from '../DocumentTypesPopover';
import { prepareCandidatePhotoMatch } from './candidatePhotoPdf';
import { popoverModifiers } from 'enums';
import { matchFilesToCandidates as matchSelectedFilesToCandidates } from './fileNameMatcher';
import {
  createUploadOperationId,
  getReplacementStatus,
  getUploadOutcomeMessage,
  isCommittedUpload,
  reconcileCandidateFileUpload,
  shouldReconcileUploadError,
} from 'Services/candidateFileUpload';
import { createQueuedUploadStatuses } from './uploadQueueHelpers';

const DocumentBulkImporter = (props) => {
  const { getCandidates, allCandidatesDetails = [], changes, setChanges } = props;
  const [files, setFiles] = useState(null);
  const [fileData, setFileData] = useState(null); // parsed data for rendering
  const [candidates, setCandidates] = useState([]); // updated list of candidates
  const [documentTypes, setDocumentTypes] = useState([]); // list of document types
  const [selectedDocumentType, setSelectedDocumentType] = useState(''); // selected document type
  const [unmatchedFiles, setUnamtchedFiles] = useState([]); // files that do not match any candidate
  const [matchedFiles, setMatchedFiles] = useState([]); // files that match a candidate
  const [failedImageExtractionFiles, setFailedImageExtractionFiles] = useState([]); // files that failed to extract images
  const [acceptedFiles, setAcceptedFiles] = useState([]); // files that are accepted

  const [uploadedFiles, setUploadedFiles] = useState([]); // files that are uploaded
  const [failedFiles, setFailedFiles] = useState([]); // files that failed to upload
  const [fileUploadStatusKeys, setFileUploadStatusKeys] = useState({});
  const [fileUploadErrors, setFileUploadErrors] = useState({});

  const [showModifyDocumentTypesModal, setShowModifyDocumentTypesModal] = useState(false);
  const [showDocumentTypeOptionsPopover, setShowDocumentTypeOptionsPopover] = useState(false);

  const [isUploadingFiles, setUploadingFiles] = useState(false);

  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);
  const [isFetchingDocumentTypes, setIsFetchingDocumentTypes] = useState(false);

  const { getTokenSilently, loginWithRedirect } = useAuth0();

  const dContext = useContext(DepartmentContext);

  const candidateOptions = useMemo(() => {
    return Object.values(allCandidatesDetails)
      .filter((candidate) => {
        return candidate?.pk_Candidate;
      })
      .sort((firstCandidate, secondCandidate) => {
        const firstName = `${firstCandidate.LastName || ''}, ${firstCandidate.FirstName || ''}`.toLowerCase();
        const secondName = `${secondCandidate.LastName || ''}, ${secondCandidate.FirstName || ''}`.toLowerCase();

        return firstName.localeCompare(secondName);
      });
  }, [allCandidatesDetails]);

  const fileUploadStatusKeysRef = useRef(fileUploadStatusKeys);
  const fileUploadErrorsRef = useRef(fileUploadErrors);
  const history = useHistory();

  useEffect(() => {
    getDocumentTypes();
  }, []);

  const getDocumentTypes = () => {
    setIsFetchingDocumentTypes(true);
    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'documentTypes',
          { pk_Department: dContext.department.pk_Department },
          formatBearerToken(token),
        )
          .then((res) => {
            setIsFetchingDocumentTypes(false);
            setDocumentTypes([
              {
                DocumentTypeName: 'Candidate Photo',
                pk_DocumentType: 'candidate_photo',
                lineAfter: true,
                isBeta: true,
                tooltip:
                  "This is a special document type for candidate photos. Choosing this and uploading images will set those as any matching candidate's profile photo. When uploading PDFs, RezRATE will attempt to extract the biggest image across the entire file, and use that as the profile image instead. Images are capped at 1000x1000 pixels ",
              },
              ...res.data,
            ]);
          })
          .catch((err) => {
            setIsFetchingDocumentTypes(false);
          });
      })
      .catch((err) => {
        setIsFetchingDocumentTypes(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const matchFilesToCandidates = async (files) => {
    const { matchedFiles: newMatchedFiles, unmatchedFiles: newUnmatchedFiles } = matchSelectedFilesToCandidates(
      files,
      allCandidatesDetails,
    );

    setAcceptedFiles(files);
    setUnamtchedFiles(
      newUnmatchedFiles.map((unmatchedFile, index) => {
        const file = unmatchedFile.file;

        return {
          ...unmatchedFile,
          uploadKey: `unmatched_${file.name}_${file.lastModified}_${file.size}_${index}`,
        };
      }),
    );

    if (selectedDocumentType === 'candidate_photo') {
      const conversionResults = await Promise.all(newMatchedFiles.map(prepareCandidatePhotoMatch));
      setMatchedFiles(conversionResults.filter((result) => result.ok).map((result) => result.file));
      setFailedImageExtractionFiles(
        conversionResults.filter((result) => !result.ok).map((result) => ({
          ...result.file,
          imageExtractionError: result.error,
          isFailedImageExtraction: true,
        })),
      );
      return;
    }

    setMatchedFiles(newMatchedFiles);
  };

  const updateUploadStatuses = (filesToUpdate, statusesByUploadKey, errorsByUploadKey = {}) => {
    const nextStatuses = { ...fileUploadStatusKeysRef.current };
    const nextErrors = { ...fileUploadErrorsRef.current };

    filesToUpdate.forEach((fileItem) => {
      const { uploadKey } = fileItem;
      nextStatuses[uploadKey] = statusesByUploadKey[uploadKey];

      if (errorsByUploadKey[uploadKey]) {
        nextErrors[uploadKey] = errorsByUploadKey[uploadKey];
      } else {
        delete nextErrors[uploadKey];
      }
    });

    fileUploadStatusKeysRef.current = nextStatuses;
    fileUploadErrorsRef.current = nextErrors;
    setFileUploadStatusKeys(nextStatuses);
    setFileUploadErrors(nextErrors);
  };

  const repackFilesForUpload = (files, isProfilePhoto) => {
    const formData = new FormData();

    files.forEach((fileItem) => {
      const currentFile = fileItem.file;

      const payload = {
        clientOperationId: fileItem.clientOperationId,
        name: currentFile.name,
        pk_Candidate: fileItem.pk_Candidate,
        pk_Department: dContext.department.pk_Department,
        pk_DocumentType: isProfilePhoto ? 0 : selectedDocumentType, // set to 0 if isProfilePhoto true
        isProfilePhoto,
      };

      formData.append(`files`, currentFile, JSON.stringify(payload));
    });
    return formData;
  };

  const chunkUpload = async (formData) => {
    return getTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...formatBearerToken(token),
          },
        };

        return axios.post(constructUrl('files/candidate/bulk-document'), formData, config)
          .then((res) => {
            return res.data;
          })
          .catch((err) => {
            return {
              isUnknown: shouldReconcileUploadError(err),
              requestError: err.response?.data?.message || err.message || 'The upload request failed.',
            };
          });
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const uploadManuallyAssignedFiles = async (filesToUpload) => {
    if (filesToUpload.length <= 0) {
      return;
    }

    let token;
    try {
      token = await getTokenSilently();
    } catch (error) {
      checkLoginForError({ err: error, loginWithRedirect });
      const failedStatuses = {};
      const uploadErrors = {};

      filesToUpload.forEach((fileItem) => {
        failedStatuses[fileItem.uploadKey] = 'failed';
        uploadErrors[fileItem.uploadKey] = 'Unable to authenticate the upload.';
      });

      updateUploadStatuses(filesToUpload, failedStatuses, uploadErrors);
      return;
    }

    const isProfilePhoto = selectedDocumentType === 'candidate_photo';
    const uploadingStatuses = {};
    filesToUpload.forEach((fileItem) => {
      uploadingStatuses[fileItem.uploadKey] = 'uploading';
    });
    updateUploadStatuses(filesToUpload, uploadingStatuses);

    await Promise.all(
      filesToUpload.map(async (fileItem) => {
        if (!fileItem.clientOperationId) {
          fileItem.clientOperationId = createUploadOperationId();
        }

        const preparation = isProfilePhoto
          ? await prepareCandidatePhotoMatch(fileItem)
          : { ok: true, file: fileItem };
        if (!preparation.ok) {
          updateUploadStatuses(
            [fileItem],
            { [fileItem.uploadKey]: 'failed' },
            { [fileItem.uploadKey]: preparation.error },
          );
          return;
        }

        try {
          const fileToUpload = preparation.file.file;
          const formData = new FormData();
          const endpoint = isProfilePhoto ? 'files/admin/candidate/photo' : 'files/candidate/document';
          const requestParams = isProfilePhoto
            ? {
                mimeType: fileToUpload.type,
                pk_Department: dContext.department.pk_Department,
                pk_Season: dContext.season.pk_Season,
                userId: fileItem.pk_Candidate,
              }
            : {};

          if (isProfilePhoto) {
            formData.append('File', fileToUpload);
          } else {
            formData.append(
              'File',
              fileItem.file,
              JSON.stringify({
                name: sanitizeFilename(fileItem.file.name),
                pk_Candidate: fileItem.pk_Candidate,
                pk_Department: dContext.department.pk_Department,
                pk_DocumentType: selectedDocumentType,
              }),
            );
          }

          const uploadResult = await axios.post(constructUrl(endpoint), formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...formatBearerToken(token),
              'x-rezrate-upload-operation-id': fileItem.clientOperationId,
            },
            params: requestParams,
          });
          if (isCommittedUpload(uploadResult.data)) {
            updateUploadStatuses([fileItem], { [fileItem.uploadKey]: 'success' });
            return;
          }

          const replacementStatus = getReplacementStatus(uploadResult.data);
          updateUploadStatuses(
            [fileItem],
            { [fileItem.uploadKey]: 'failed' },
            {
              [fileItem.uploadKey]: getUploadOutcomeMessage({
                isProfilePhoto,
                replacementStatus,
              }),
            },
          );
        } catch (error) {
          let replacementStatus = getReplacementStatus(error.response?.data);

          if (shouldReconcileUploadError(error)) {
            try {
              const reconciliation = await reconcileCandidateFileUpload({
                clientOperationId: fileItem.clientOperationId,
                headers: formatBearerToken(token),
                isProfilePhoto,
                pk_Candidate: fileItem.pk_Candidate,
                pk_Department: dContext.department.pk_Department,
                pk_DocumentType: selectedDocumentType,
              });

              if (reconciliation.committed) {
                updateUploadStatuses([fileItem], { [fileItem.uploadKey]: 'success' });
                return;
              }
            } catch (reconciliationError) {
              // Keep the file actionable until a later retry can reconcile it.
            }

            replacementStatus = 'unknown_reconciliation_required';
          }

          updateUploadStatuses(
            [fileItem],
            { [fileItem.uploadKey]: 'failed' },
            {
              [fileItem.uploadKey]:
                getUploadOutcomeMessage({ isProfilePhoto, replacementStatus }) ||
                error.response?.data?.message ||
                error.message ||
                'The upload request failed.',
            },
          );
        }
      }),
    );
  };

  const fileUploadBulk = async (filesToUpload) => {
    const filesAsArray = [...filesToUpload];
    const automaticallyMatchedFiles = filesAsArray.filter((fileItem) => {
      return !fileItem.isManuallyAssigned;
    });
    const manuallyAssignedFiles = filesAsArray.filter((fileItem) => {
      return fileItem.isManuallyAssigned;
    });
    const chunkSize = 5;

    setUploadingFiles(true);
    const queuedStatuses = createQueuedUploadStatuses(filesAsArray);
    // Render the entire upload queue before the first request begins.
    updateUploadStatuses(filesAsArray, queuedStatuses);

    const isProfilePhoto = selectedDocumentType === 'candidate_photo';

    for (let i = 0; i < automaticallyMatchedFiles.length; i += chunkSize) {
      const filesChunk = automaticallyMatchedFiles.slice(i, i + chunkSize);
      filesChunk.forEach((fileItem) => {
        if (!fileItem.clientOperationId) {
          fileItem.clientOperationId = createUploadOperationId();
        }
      });
      const formData = repackFilesForUpload(filesChunk, isProfilePhoto);
      const uploadingStatuses = {};
      filesChunk.forEach((fileItem) => {
        uploadingStatuses[fileItem.uploadKey] = 'uploading';
      });
      updateUploadStatuses(filesChunk, uploadingStatuses);

      const chunkResult = await chunkUpload(formData);
      const results = Array.isArray(chunkResult?.results) ? chunkResult.results : [];
      const completedStatuses = {};
      const uploadErrors = {};

      const reconciliationResults = chunkResult?.isUnknown
        ? await Promise.all(
            filesChunk.map(async (fileItem) => {
              try {
                const token = await getTokenSilently();
                return reconcileCandidateFileUpload({
                  clientOperationId: fileItem.clientOperationId,
                  headers: formatBearerToken(token),
                  isProfilePhoto,
                  pk_Candidate: fileItem.pk_Candidate,
                  pk_Department: dContext.department.pk_Department,
                  pk_DocumentType: selectedDocumentType,
                });
              } catch (reconciliationError) {
                return null;
              }
            }),
          )
        : [];

      filesChunk.forEach((fileItem, fileIndex) => {
        const expectedDocumentType = isProfilePhoto ? 0 : selectedDocumentType;
        const matchingResult = results.find((result) => {
          return (
            `${result.name}` === `${fileItem.file.name}` &&
            `${result.pk_Candidate}` === `${fileItem.pk_Candidate}` &&
            `${result.pk_DocumentType}` === `${expectedDocumentType}`
          );
        });
        const reconciliationResult = reconciliationResults[fileIndex];

        if (
          (matchingResult?.ok === true && isCommittedUpload(matchingResult)) ||
          reconciliationResult?.committed === true
        ) {
          completedStatuses[fileItem.uploadKey] = 'success';
          return;
        }

        completedStatuses[fileItem.uploadKey] = 'failed';
        const replacementStatus = chunkResult?.isUnknown
          ? 'unknown_reconciliation_required'
          : getReplacementStatus(matchingResult);
        uploadErrors[fileItem.uploadKey] =
          getUploadOutcomeMessage({
            isProfilePhoto,
            replacementStatus,
          }) ||
          matchingResult?.error ||
          chunkResult?.requestError ||
          'No per-file upload result was returned.';
      });

      updateUploadStatuses(filesChunk, completedStatuses, uploadErrors);
      setFailedFiles((previousFailedFiles) => {
        const failedFilesByKey = new Map();
        [...previousFailedFiles, ...filesChunk].forEach((fileItem) => {
          if (fileUploadStatusKeysRef.current[fileItem.uploadKey] === 'failed') {
            failedFilesByKey.set(fileItem.uploadKey, fileItem);
          }
        });
        return Array.from(failedFilesByKey.values());
      });
    }

    await uploadManuallyAssignedFiles(manuallyAssignedFiles);

    const { successCount, totalCount } = countFailedOrUpdatingFiles();
    if (totalCount === successCount) {
      setChanges({ ...changes, import_data: false });
    }
    getCandidates();
    setUploadingFiles(false);
  };

  const countFailedOrUpdatingFiles = () => {
    let successCount = 0;
    let totalCount = 0;
    Object.keys(fileUploadStatusKeysRef.current).forEach((key) => {
      totalCount++;
      if (fileUploadStatusKeysRef.current[key] === 'success') {
        successCount++;
      }
    });

    return { successCount, totalCount };
  };

  const resetBulkImports = () => {
    fileUploadStatusKeysRef.current = {};
    fileUploadErrorsRef.current = {};
    setFileUploadStatusKeys({});
    setFileUploadErrors({});
    setFailedFiles([]);
    setMatchedFiles([]);
    setUnamtchedFiles([]);
    setFailedImageExtractionFiles([]);
    setAcceptedFiles([]);
    setSelectedDocumentType('');
    setChanges({ ...changes, import_data: false });
  };

  const onDrop = (acceptedFiles) => {
    const isDisabled = isFetchingDocumentTypes || !selectedDocumentType;

    if (isDisabled) {
      return;
    }

    let hasNonZeroFile = 0; // if false. we pop a swal

    acceptedFiles.forEach((file) => {
      if (file.size > 0) {
        hasNonZeroFile++;
      }
    });

    if (!acceptedFiles || acceptedFiles.length <= 0) {
      Swal.fire({
        title: 'Invalid File Type',
        icon: 'error',
        type: 'error',
        text: 'Please Use PDF, JPG, JPEG or PNG',
      });
    } else if (hasNonZeroFile == 0) {
      Swal.fire({
        title: 'Unable to read file.' + acceptedFiles.length > 1 ? 's' : '',
        text: 'Make sure you have Extracted/Unzipped your files before uploading.1',
        icon: 'error',
      });
      return;
    } else if (hasNonZeroFile < acceptedFiles.length) {
      Swal.fire({
        title: 'Some files are empty',
        text: 'Make sure you have Extracted/Unzipped your files before uploading.2',
        icon: 'warning',
      });
    } else {
      setChanges({ ...changes, import_data: true });
      matchFilesToCandidates(acceptedFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'],
    onDrop,
  });

  const renderModifyDocumentTypesModal = () => {
    return (
      <Modal
        style={{ minWidth: '70vw' }}
        centered
        isOpen={showModifyDocumentTypesModal}
        toggle={() => {
          if (showModifyDocumentTypesModal) {
            getDocumentTypes();
          }
          setShowModifyDocumentTypesModal(!showModifyDocumentTypesModal);
        }}
      >
        <ModalHeader
          toggle={() => {
            if (showModifyDocumentTypesModal) {
              getDocumentTypes();
            }
            setShowModifyDocumentTypesModal(!showModifyDocumentTypesModal);
          }}
        >
          Modify Document Types
        </ModalHeader>
        <ModalBody style={{ height: '60vh' }}>
          <div style={{ ...style.simpleRow, overflowY: 'auto', height: '56vh' }}>
            <DocumentTypesPopover />
          </div>
        </ModalBody>
      </Modal>
    );
  };

  const renderUploadStatus = () => {
    const filesWithUploadStatus = [...matchedFiles, ...unmatchedFiles].filter((fileItem) => {
      return fileUploadStatusKeys[fileItem.uploadKey];
    });

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '80%',
          paddingTop: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: 10,
            height: 'calc(100vh - 550px)',
            overflowY: 'auto',
          }}
        >
          {filesWithUploadStatus.map((item) => {
            return (
              <FileToUploadItem
                item={item}
                fileUploadErrors={fileUploadErrors}
                fileUploadStatusKeys={fileUploadStatusKeys}
                fileUploadBulk={fileUploadBulk}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderUploadPreview = () => {
    const selectCandidateForUnmatchedFile = (uploadKey, candidatePk) => {
      setUnamtchedFiles((previousUnmatchedFiles) => {
        return previousUnmatchedFiles.map((unmatchedFile) => {
          if (unmatchedFile.uploadKey !== uploadKey) {
            return unmatchedFile;
          }

          return {
            ...unmatchedFile,
            selectedCandidatePk: candidatePk || null,
          };
        });
      });
    };

    const confirmCandidateForUnmatchedFile = (uploadKey, candidatePk) => {
      const selectedCandidate = candidateOptions.find((candidate) => {
        return `${candidate.pk_Candidate}` === `${candidatePk}`;
      });

      setUnamtchedFiles((previousUnmatchedFiles) => {
        return previousUnmatchedFiles.map((unmatchedFile) => {
          if (unmatchedFile.uploadKey !== uploadKey) {
            return unmatchedFile;
          }

          return {
            ...unmatchedFile,
            FirstName: selectedCandidate?.FirstName || '',
            LastName: selectedCandidate?.LastName || '',
            isManuallyAssigned: Boolean(selectedCandidate),
            pk_Candidate: selectedCandidate?.pk_Candidate || null,
            selectedCandidatePk: selectedCandidate?.pk_Candidate || null,
          };
        });
      });
    };

    const removeUnmatchedFile = (unmatchedFile) => {
      const { file, uploadKey } = unmatchedFile;

      setUnamtchedFiles((previousUnmatchedFiles) => {
        return previousUnmatchedFiles.filter((previousUnmatchedFile) => {
          return previousUnmatchedFile.uploadKey !== uploadKey;
        });
      });
      setAcceptedFiles((previousAcceptedFiles) => {
        return previousAcceptedFiles.filter((acceptedFile) => {
          return acceptedFile !== file;
        });
      });
      setFailedFiles((previousFailedFiles) => {
        return previousFailedFiles.filter((failedFile) => {
          return failedFile.uploadKey !== uploadKey;
        });
      });
      const nextStatuses = { ...fileUploadStatusKeysRef.current };
      const nextErrors = { ...fileUploadErrorsRef.current };
      delete nextStatuses[uploadKey];
      delete nextErrors[uploadKey];
      fileUploadStatusKeysRef.current = nextStatuses;
      fileUploadErrorsRef.current = nextErrors;
      setFileUploadStatusKeys(nextStatuses);
      setFileUploadErrors(nextErrors);
    };

    const renderFileItem = (item, isUnmatchedFile = false) => {
      const { file, LastName, FirstName, isFailedImageExtraction, imageExtractionError } = item;
      const { name } = file;
      const nameToDisplay = `${LastName || ''}${LastName && FirstName ? ', ' : ''}${FirstName || ''}`;
      const isCandidateAssignmentPending =
        isUnmatchedFile && `${item.selectedCandidatePk || ''}` !== `${item.pk_Candidate || ''}`;

      return (
        <div style={{ display: 'flex', flexDirection: 'row', padding: 10, borderBottom: '1px solid #D0D0D0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', width: 80, marginRight: 10 }}>
            {file.type.includes('image/') && isFailedImageExtraction == false ? (
              <img src={URL.createObjectURL(file)} style={{ width: 70 }} />
            ) : file.type.includes('image/') ? (
              <FontAwesomeIcon icon={faFileImage} style={{ fontSize: 70 }} />
            ) : (
              <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: 70 }} />
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', width: 'calc(100% - 80px)' }}>
            <div style={{ fontSize: 15, fontWeight: 'bold' }}>{nameToDisplay || '---'}</div>
            <div style={{ fontSize: 13 }}> {name || '---'}</div>
            {imageExtractionError && <div style={{ color: '#A00000', fontSize: 13 }}>{imageExtractionError}</div>}
            {isUnmatchedFile ? (
              <div style={{ marginTop: 8 }}>
                <Input
                  type="select"
                  value={item.selectedCandidatePk || item.pk_Candidate || ''}
                  onChange={(event) => {
                    selectCandidateForUnmatchedFile(item.uploadKey, event.target.value);
                  }}
                >
                  <option value="">Select candidate</option>
                  {candidateOptions.map((candidate) => {
                    const candidateName = `${candidate.LastName || ''}${
                      candidate.LastName && candidate.FirstName ? ', ' : ''
                    }${candidate.FirstName || ''}`;

                    return (
                      <option key={candidate.pk_Candidate} value={candidate.pk_Candidate}>
                        {candidateName || `Candidate ${candidate.pk_Candidate}`}
                      </option>
                    );
                  })}
                </Input>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <Button
                    color="primary"
                    disabled={!item.selectedCandidatePk || !isCandidateAssignmentPending}
                    onClick={() => {
                      confirmCandidateForUnmatchedFile(item.uploadKey, item.selectedCandidatePk);
                    }}
                    size="sm"
                    type="button"
                  >
                    Confirm
                  </Button>
                  <Button
                    color="danger"
                    onClick={() => {
                      removeUnmatchedFile(item);
                    }}
                    outline
                    size="sm"
                    type="button"
                  >
                    Remove
                  </Button>
                  <div
                    style={{ color: item.pk_Candidate && !isCandidateAssignmentPending ? '#198754' : '#A65E00', fontSize: 12 }}
                  >
                    {item.pk_Candidate && !isCandidateAssignmentPending
                      ? 'Candidate assigned'
                      : item.selectedCandidatePk
                      ? 'Confirm candidate before importing'
                      : 'Candidate required before importing'}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      );
    };

    let totalColumns = 0;
    if (unmatchedFiles.length > 0) {
      totalColumns++;
    }

    if (failedImageExtractionFiles.length > 0) {
      totalColumns++;
    }

    if (matchedFiles.length > 0) {
      totalColumns++;
    }

    let columnWidth = '90%';

    return (
      <div style={{ display: 'flex', flexDirection: 'row', width: '100%', paddingTop: 0 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              borderTop: '1px solid black',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {unmatchedFiles.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: columnWidth,
                  padding: 10,
                  fontWeight: 'bold',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#FF0000',
                  fontSize: 17,
                }}
              >
                {`${unmatchedFiles.length} / ${acceptedFiles.length} Unmatched`}
              </div>
            ) : null}

            {failedImageExtractionFiles.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: columnWidth,
                  padding: 10,
                  fontWeight: 'bold',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#FF0000',
                  fontSize: 17,
                }}
              >
                {`${failedImageExtractionFiles.length} / ${acceptedFiles.length} Failed Image${
                  failedImageExtractionFiles.length > 1 ? 's' : ''
                }`}
              </div>
            ) : null}

            {matchedFiles.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: columnWidth,
                  padding: 10,
                  fontWeight: 'bold',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#00AE28',
                  fontSize: 17,
                }}
              >
                {`${matchedFiles.length} / ${acceptedFiles.length} Matches`}
              </div>
            ) : null}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {unmatchedFiles.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: columnWidth,
                  height: 'calc(100vh - 550px)',
                  overflowY: 'auto',
                  marginRight: 10,
                  marginLeft: 10,
                  backgroundColor: '#FFD2D2',
                }}
              >
                {unmatchedFiles.length > 0 ? (
                  unmatchedFiles.map((item) => renderFileItem(item, true))
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      padding: 10,
                    }}
                  >
                    No Unmatched Files
                  </div>
                )}
              </div>
            ) : null}

            {failedImageExtractionFiles.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: columnWidth,
                  height: 'calc(100vh - 550px)',
                  overflowY: 'auto',
                  marginRight: 10,
                  marginLeft: 10,
                  backgroundColor: '#FFD2D2',
                }}
              >
                {failedImageExtractionFiles.length > 0 ? (
                  failedImageExtractionFiles.map((item) => renderFileItem(item))
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      padding: 10,
                    }}
                  >
                    No Failed Image Extraction Files
                  </div>
                )}
              </div>
            ) : null}

            {matchedFiles.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: columnWidth,
                  height: 'calc(100vh - 550px)',
                  overflowY: 'auto',
                  marginRight: 10,
                  marginLeft: 10,
                  backgroundColor: '#CEFFDA',
                }}
              >
                {matchedFiles.length > 0 ? (
                  matchedFiles.map((item) => renderFileItem(item))
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      padding: 10,
                    }}
                  >
                    No Matching Files
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderDragAndDropArea = () => {
    const isDisabled = isFetchingDocumentTypes || !selectedDocumentType;

    const selectedDocumentTypeObject = documentTypes.find((d) => {
      return d.pk_DocumentType === selectedDocumentType;
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={style.simpleColumn}>
          <div style={{ ...style.simpleRow, paddingLeft: 20, paddingRight: 20, justifyContent: 'center' }}>
            <div style={{ ...style.simpleColumn, width: '100%' }}>
              <Button
                outline={selectedDocumentTypeObject ? false : true}
                id={'document_type_btn'}
                style={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                color={selectedDocumentTypeObject ? 'secondary' : 'danger'}
                onClick={() => {
                  setShowDocumentTypeOptionsPopover(!showDocumentTypeOptionsPopover);
                }}
              >
                <span style={{ flex: 1, textAlign: 'center' }}>
                  {selectedDocumentTypeObject
                    ? selectedDocumentTypeObject.DocumentTypeName || 'Unnamed Document Type'
                    : 'Select Document Type'}
                  {selectedDocumentTypeObject ? null : (
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginLeft: 5 }} />
                  )}
                </span>
                <FontAwesomeIcon icon={faSortDown} style={{ marginLeft: 'auto' }} />
              </Button>
              {renderDocumentTypeOptionsPopover()}
            </div>
            <div
              style={{
                ...style.simpleColumn,
                width: 230,
                paddingLeft: 5,
                paddingRight: 5,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                color="primary"
                style={{ fontWeight: 'bold', width: 220 }}
                onClick={() => {
                  setShowModifyDocumentTypesModal(true);
                  // history.push('/administrator/settings/candidateSettings/document_types');
                }}
              >
                Modify Document Types
              </Button>
            </div>
          </div>
          <div style={{ ...style.simpleRow, justifyContent: 'center' }}>
            <div
              disabled={isDisabled}
              {...getRootProps()}
              style={{
                height: 'min-content',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                borderRadius: 15,
              }}
              className="hover_blue"
            >
              <input disabled={isDisabled} {...getInputProps()} />
              <div
                style={{
                  height: 'calc(70vh - 250px',
                  width: 'calc(70vw - 250px)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  margin: 10,
                  padding: 10,
                  backgroundColor: isDisabled ? '#94908f' : '#cfd0d1',
                  border: '3px solid black',
                  borderRadius: 15,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                {isFetchingDocumentTypes || !selectedDocumentType ? (
                  // <Loading />
                  <FontAwesomeIcon icon={faExclamationTriangle} size="10x" style={{ paddingBottom: '15px' }} />
                ) : (
                  <FontAwesomeIcon icon={faUpload} size="10x" style={{ paddingBottom: '15px' }} />
                )}
                {isFetchingDocumentTypes ? (
                  <h4>Please wait. . .</h4>
                ) : !selectedDocumentType ? (
                  'Please select a document type to upload'
                ) : isDragActive ? (
                  <h4>Drop the files here ...</h4>
                ) : (
                  <h4>
                    {' '}
                    {isFetchingDocumentTypes
                      ? 'Fetching document types. . .'
                      : 'Click to browse or drag and drop files here'}
                  </h4>
                )}
                {selectedDocumentType ? (
                  <h4>{`(${
                    documentTypes.find((docType) => {
                      return (
                        docType.pk_DocumentType === parseInt(selectedDocumentType) ||
                        docType.pk_DocumentType === selectedDocumentType
                      );
                    }).DocumentTypeName
                  })`}</h4>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentTypeOptionsPopover = () => {
    return (
      <Popover
        isOpen={showDocumentTypeOptionsPopover}
        toggle={() => {
          setShowDocumentTypeOptionsPopover(!showDocumentTypeOptionsPopover);
        }}
        flip={false}
        modifiers={popoverModifiers}
        placement="bottom"
        target="document_type_btn"
        placementPrefix="filterPopover bs-popover"
        trigger="legacy"
      >
        <PopoverBody>
          <div style={{ ...style.simpleColumn, width: '100%', height: 420, overflowY: 'auto' }}>
            {!isFetchingDocumentTypes ? (
              documentTypes.map((item) => {
                const { lineAfter, tooltip, isBeta } = item;
                return (
                  <>
                    <div
                      style={{ ...style.simpleRow, justifyContent: 'space-between', padding: 5 }}
                      onClick={() => {
                        setSelectedDocumentType(item.pk_DocumentType);
                        setShowDocumentTypeOptionsPopover(false);
                      }}
                    >
                      <Button
                        outline
                        color={selectedDocumentType == item.pk_DocumentType ? 'primary' : 'secondary'}
                        style={{ width: '100%', fontWeight: 'bold' }}
                      >
                        {item.DocumentTypeName || `Unnamed Document Type`}{' '}
                        {isBeta ? <span style={{ fontSize: 11, fontWeight: 'bold', color: 'purple' }}>BETA</span> : ' '}
                        {tooltip ? (
                          <FontAwesomeIcon id={`tooltip_${item.pk_DocumentType}`} icon={faInfoCircle} />
                        ) : null}
                        {tooltip ? (
                          <UncontrolledTooltip
                            target={`tooltip_${item.pk_DocumentType}`}
                            placement="right"
                            modifiers={popoverModifiers}
                          >
                            {' '}
                            {tooltip}
                          </UncontrolledTooltip>
                        ) : null}
                      </Button>
                    </div>
                    {lineAfter ? (
                      <div
                        style={{
                          height: 2,
                          width: '100%',
                          marginTop: 15,
                          marginBottom: 10,
                          borderTop: '1px lightgray solid',
                        }}
                      >
                        {' '}
                      </div>
                    ) : null}
                  </>
                );
              })
            ) : (
              <div style={{ ...style.simpleRow, justifyContent: 'center', padding: 5 }}>
                Loading document types. . .
              </div>
            )}
          </div>
        </PopoverBody>
      </Popover>
    );
  };

  const renderContent = () => {
    if (Object.keys(fileUploadStatusKeys).length > 0 || isUploadingFiles) {
      return renderUploadStatus();
    } else if (matchedFiles.length > 0 || unmatchedFiles.length > 0 || failedImageExtractionFiles.length > 0) {
      return renderUploadPreview();
    } else {
      return renderDragAndDropArea();
    }
  };

  const renderTopMessage = () => {
    if (matchedFiles.length > 0 || unmatchedFiles.length > 0) {
      return (
        <div style={{ ...style.simpleColumn, justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ fontSize: 15, textAlign: 'center', marginBottom: 5 }}>
            Associate documents (such as CV or LoR) with the appropriate candidate. The <b>candidate must exist</b>{' '}
            before the document can be paired.
          </div>
          <div>
            <div style={{ fontSize: 17, textAlign: 'center', fontWeight: 'bold', color: '#8383f7' }}>
              {selectedDocumentType
                ? `${
                    documentTypes.find((docType) => {
                      return (
                        docType.pk_DocumentType === parseInt(selectedDocumentType) ||
                        docType.pk_DocumentType === selectedDocumentType
                      );
                    }).DocumentTypeName
                  }`
                : null}
            </div>
          </div>
        </div>
      );
    }
  };

  const renderBottomBar = () => {
    const isUnmatchedFileConfirmed = (unmatchedFile) => {
      return (
        unmatchedFile.isManuallyAssigned &&
        unmatchedFile.pk_Candidate &&
        `${unmatchedFile.selectedCandidatePk || ''}` === `${unmatchedFile.pk_Candidate}`
      );
    };
    const filesReadyToUpload = [
      ...matchedFiles,
      ...unmatchedFiles.filter((unmatchedFile) => {
        return isUnmatchedFileConfirmed(unmatchedFile);
      }),
    ];
    const unconfirmedUnmatchedFiles = unmatchedFiles.filter((unmatchedFile) => {
      return !isUnmatchedFileConfirmed(unmatchedFile);
    });
    const hasUnconfirmedUnmatchedFiles = unconfirmedUnmatchedFiles.length > 0;

    if (Object.keys(fileUploadStatusKeys).length > 0) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            width: '100%',
            padding: 10,
          }}
        >
          <Button
            disabled={filesReadyToUpload.length <= 0}
            color={'success'}
            style={{ width: 250, fontWeight: 'bold' }}
            onClick={() => {
              const { totalCount, successCount } = countFailedOrUpdatingFiles();
              if (isUploadingFiles) {
                Swal.fire({
                  title: 'Please Wait',
                  icon: 'warning',
                  text: 'Some files are still uploading or failed to upload. Are you sure you want to ignore them?',
                  showCancelButton: true,
                  confirmButtonText: 'Yes',
                  confirmButtonColor: '#FF0000',
                }).then((result) => {
                  if (result.isConfirmed) {
                    resetBulkImports();
                  }
                });
              } else if (totalCount != successCount) {
                Swal.fire({
                  title: 'Please Wait',
                  icon: 'warning',
                  text: 'Some files have failed to upload. Are you sure you want to ignore them?',
                  showCancelButton: true,
                  confirmButtonText: 'Yes',
                  confirmButtonColor: '#FF0000',
                }).then((result) => {
                  if (result.isConfirmed) {
                    resetBulkImports();
                  }
                });
              } else {
                resetBulkImports();
              }
            }}
          >
            Upload more files
          </Button>
        </div>
      );
    } else if (matchedFiles.length > 0 || unmatchedFiles.length > 0 || failedImageExtractionFiles.length > 0) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: 10,
          }}
        >
          {hasUnconfirmedUnmatchedFiles ? (
            <div style={{ color: '#B42318', fontWeight: 'bold', marginBottom: 8, textAlign: 'center' }}>
              {unconfirmedUnmatchedFiles.length} unmatched file{unconfirmedUnmatchedFiles.length === 1 ? '' : 's'} still need
              a confirmed candidate.
            </div>
          ) : null}
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
            <Button
              color={'secondary'}
              style={{ width: 250, fontWeight: 'bold' }}
              onClick={() => {
                resetBulkImports();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={filesReadyToUpload.length <= 0 || hasUnconfirmedUnmatchedFiles}
              color={'success'}
              style={{ width: 250, fontWeight: 'bold' }}
              onClick={() => {
                fileUploadStatusKeysRef.current = {};
                fileUploadErrorsRef.current = {};
                setFileUploadStatusKeys({});
                setFileUploadErrors({});
                fileUploadBulk(filesReadyToUpload);
              }}
            >
              Import {filesReadyToUpload.length} Document{filesReadyToUpload.length > 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <div style={{ ...style.simpleRow, margin: 10, backgroundColor: 'var(--rr-surface)', width: '98%', minHeight: '60vh' }}>
      <div style={{ ...style.simpleColumn, justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
        {/* <img id="debugger_img" src={null} /> */}
        <div style={{ ...style.simpleRow, justifyContent: 'center' }}>
          <div style={{ ...style.simpleColumn, alignItems: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 'bold', marginBottom: 5, padding: 5, textAlign: 'center' }}>
              Bulk Document Import {isUploadingFiles ? 'Uploading. . .' : null}
            </div>
            <div style={{ ...style.simpleRow, width: '80%', justifyContent: 'center' }}>{renderTopMessage()}</div>
          </div>
        </div>
        {renderContent()}
        {renderBottomBar()}
        {renderModifyDocumentTypesModal()}
      </div>
    </div>
  );
};

const FileToUploadItem = (props) => {
  const { item, fileUploadErrors, fileUploadStatusKeys, fileUploadBulk } = props;
  const { file, LastName, FirstName, pk_Candidate, isFailedImageExtraction } = item;

  const { name, type } = file;
  const nameToDisplay = `${LastName || ''}${LastName && FirstName ? ', ' : ''}${FirstName || ''}`;
  const status = fileUploadStatusKeys[item.uploadKey] || 'default';
  const retryTooltipTargetId = `bulk_upload_retry_${Array.from(`${item.uploadKey}`).map((character) => {
    return character.charCodeAt(0).toString(36);
  }).join('_')}`;

  let icons = {
    success: faCheckCircle,
    failed: faRedoAlt,
    uploading: faSpinner,
    queued: faClock,
    default: faClock,
  };

  let backgroundColors = {
    success: '#CEFFDA',
    failed: '#FFD2D2',
    uploading: '#C0C0C0',
    queued: '#FFA459',
    default: '#ffa459',
  };

  let messages = {
    success: 'Succesfully Uploaded',
    failed: fileUploadErrors[item.uploadKey] || 'Failed to Upload',
    uploading: 'Uploading. . .',
    queued: 'Queued to Upload',
    default: 'Waiting to Upload',
  };
  let backgroundColor = backgroundColors[status] || backgroundColors.default;
  let icon = icons[status] || icons.default;
  let message = messages[status] || messages.default;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        padding: 10,
        borderBottom: '1px solid #D0D0D0',
        backgroundColor,
        width: '100%',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', width: 80, marginRight: 10, justifyContent: 'center' }}>
        {file.type.includes('image/') && isFailedImageExtraction == false ? (
          <img src={URL.createObjectURL(file)} style={{ width: 70 }} />
        ) : file.type.includes('image/') ? (
          <FontAwesomeIcon icon={faFileImage} style={{ fontSize: 70 }} />
        ) : (
          <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: 70 }} />
        )}
        {/* <FontAwesomeIcon icon={type.includes('image/') ? faFileImage : faFilePdf} style={{ fontSize: 30 }} /> */}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <div
          style={{ fontSize: 15, fontWeight: 'bold' }}
          onClick={() => {
            console.log('item: ', item);

            // window
            // .open(`/viewFile/${selectedCandidate.pk_Candidate}/${pk_File}/${pk_DocumentType}/${Name}`, '_blank', 'noopener,noreferrer')
            // .focus();
          }}
        >
          {nameToDisplay || '---'}
        </div>
        <div style={{ fontSize: 13 }}> {name || '---'}</div>
      </div>
      <div
        style={{ display: 'flex', flexDirection: 'column', width: 250, justifyContent: 'center', fontWeight: 'bold' }}
      >
        {message}
      </div>
      <div
        id={retryTooltipTargetId}
        style={{ display: 'flex', flexDirection: 'column', width: 80, justifyContent: 'center' }}
      >
        <FontAwesomeIcon
          icon={icon}
          spin={status === 'uploading'}
          style={{
            fontSize: 30,
            cursor: status === 'failed' ? 'pointer' : 'default',
          }}
          onClick={() => {
            if (status === 'failed') {
              fileUploadBulk([item]);
            }
          }}
        />
        {status === 'failed' && (
          <UncontrolledTooltip placement="top" target={retryTooltipTargetId}>
            Click to Retry Upload of this file
          </UncontrolledTooltip>
        )}
      </div>
    </div>
  );
};

export default DocumentBulkImporter;
